/**
 * Tab "Bài tập" — list assignments for the session's lesson.
 * Per-assignment publish/close action. Read submission counts.
 */
import { toast } from 'sonner';
import { ClipboardList } from 'lucide-react';
import { AssignmentRow } from './assignment-row';
import { useUpdateAssignmentStatus } from '@client/lib/queries/use-assignment-status';
import type { DashboardAssignment } from '@client/lib/queries/use-session-dashboard';
import type { AssignmentStatus } from '@client/lib/queries/use-assignment-status';

interface TabAssignmentsProps {
  classId: string;
  sessionId: string;
  assignments: DashboardAssignment[];
}

export function TabAssignments({ classId, sessionId, assignments }: TabAssignmentsProps) {
  const updateStatus = useUpdateAssignmentStatus(classId, sessionId);

  async function handleStatusChange(id: string, status: AssignmentStatus) {
    try {
      await updateStatus.mutateAsync({ id, status });
      const label = status === 'published' ? 'xuất bản' : 'đóng nộp';
      toast.success(`Đã ${label} bài tập.`);
    } catch {
      toast.error('Lỗi khi cập nhật trạng thái bài tập.');
    }
  }

  if (assignments.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <ClipboardList className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          Bài học này chưa có bài tập nào.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {assignments.map((a) => (
        <AssignmentRow
          key={a.id}
          assignment={a}
          onStatusChange={(id, status) => void handleStatusChange(id, status)}
          isPending={updateStatus.isPending && updateStatus.variables?.id === a.id}
        />
      ))}
    </div>
  );
}
